import React, {
  ReactNode,
  useState,
  MouseEvent,
  useId,
  CSSProperties,
  useEffect,
  useRef,
} from 'react';
import {
  Button,
  Menu,
  MenuItem,
  MenuProps,
  ButtonProps,
  Typography,
} from '@mui/material';
import {ReactComponent as DropDownIcon} from '../../../../src/assests/svg/drop_down.svg';
import {ReactComponent as DropUpIcon} from '../../../../src/assests/svg/drop_up.svg';
import AurIcon, {IconColors, IconNames} from '../aur-icon/AurIcon';
import AurLoadingIndicator from '../aur-loading-indicator/AurLoadingIndictor';
import {t} from 'i18next';

export enum DropdownIconTypes {
  Caret = 'Caret',
  Chev = 'Chev',
}

// Define the type for DropdownButton props
export interface AurDropdownButtonProps {
  buttonText?: string;
  type: string;
  menuItems: {label: string; onClick?: () => void; disabled?: boolean}[];
  startIcon?: ReactNode; // Use ReactNode for icons
  endIcon?: ReactNode; // Use ReactNode for icons
  buttonProps?: ButtonProps; // Use ButtonProps type for button props
  menuProps?: MenuProps; // Additional menu props
  size?: 'Large' | 'Medium' | 'Small';
  classNames?: string;
  iconType: 'Arrow' | 'Other';
  menuDirectionLeft?: boolean;
  iconSize?: 'large' | 'medium' | 'small';
  iconClassName?: string; // custom classname for icon
  menuItemHeight?: 'large' | 'medium' | 'small'; // custom height for menu item
  menuItemWidth?:
    | 'Xlarge'
    | 'large'
    | 'mediumSmall'
    | 'mediumLarge'
    | 'medium'
    | 'small'; // custom width for menu item
  menuItemCustomWidth?: `${number}px` | null;
  menuItemTextSize?: 'large' | 'small'; // custom text size for menu item
  dropdownRole?: string;
  ariaLabelledby?: string;
  hintText?: string;
  title?: string;
  menuClassNames?: string;
  elementID?: string;
  showLoader?: boolean;
  buttonTitle?: string;
  buttonTitleClass?: string; // custom classname for buttonTitle
  dropDownIconType?: DropdownIconTypes;
  menuItemTextColor?: string;
  renderTooltipWrapper?: (
    menuItem: React.ReactElement,
    disabled?: boolean
  ) => React.ReactElement; // If want to render tooltip on menu button
  menuItemStyle?: CSSProperties;
}

export const AurDropdownButton: React.FC<
  React.PropsWithChildren<AurDropdownButtonProps>
> = ({
  buttonText,
  type,
  menuItems,
  startIcon,
  endIcon,
  buttonProps,
  menuProps,
  size = 'Large',
  classNames = '',
  iconType,
  menuDirectionLeft = true,
  children,
  iconSize = 'medium',
  iconClassName,
  menuItemHeight = 'medium',
  menuItemWidth = 'medium',
  menuItemCustomWidth = null,
  menuItemTextSize = 'small',
  dropdownRole = '',
  ariaLabelledby = 'addUserButton',
  hintText,
  title = '',
  menuClassNames,
  elementID,
  showLoader = false,
  buttonTitle,
  buttonTitleClass,
  dropDownIconType = DropdownIconTypes.Caret,
  menuItemTextColor,
  renderTooltipWrapper,
  menuItemStyle = {},
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const hintId = useId();
  const open = Boolean(anchorEl);
  const menuRef = useRef<HTMLButtonElement | null>(null);
  const menuItemsHeight = {
    small: '32px',
    medium: '36px',
    large: '48px',
  };
  const menuItemsWidth = {
    small: '91px',
    medium: '160px',
    mediumSmall: '180px',
    mediumLarge: '240px',
    large: '260px',
    Xlarge: '320px',
  };
  const menuItemsTextSize = {
    small: 'body3',
    large: 'body2',
  };

  startIcon
    ? (startIcon =
        isOpen && iconType === 'Arrow' ? (
          <DropUpIcon aria-hidden="true" />
        ) : iconType === 'Arrow' ? (
          <DropDownIcon aria-hidden="true" />
        ) : (
          startIcon
        ))
    : endIcon
      ? (endIcon =
          isOpen && iconType === 'Arrow' ? (
            <DropUpIcon aria-hidden="true" />
          ) : iconType === 'Arrow' ? (
            <DropDownIcon  aria-hidden="true" />
          ) : (
            endIcon
          ))
      : '';

  children =
    children && iconType === 'Arrow' ? (
      isOpen ? (
        <AurIcon
          icon={
            dropDownIconType === DropdownIconTypes.Caret
              ? IconNames.CaretUp
              : IconNames.ChevronUp
          }
          color={IconColors.Custom}
          size={iconSize}
          customClassName={iconClassName}
        />
      ) : (
        <AurIcon
          icon={
            dropDownIconType === DropdownIconTypes.Caret
              ? IconNames.CaretDown
              : IconNames.ChevronDown
          }
          color={IconColors.Custom}
          size={iconSize}
          customClassName={iconClassName}
        />
      )
    ) : (
      children
    );

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setIsOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setIsOpen(false);
    requestAnimationFrame(() => {     
       menuRef?.current?.focus();    
    });
 
  };

  const variant =
    type === 'exit' ? 'outlined' : type === 'text' ? 'text' : 'contained';

  const renderMenuItem = (
    item: {label: string; onClick?: () => void; disabled?: boolean},
    index: number
  ) => {
    const menuItem = (
      <MenuItem
        className={`aurDropDownMenuItems typo-text ${menuItemsTextSize[menuItemTextSize]}_size`}
        key={index}
        sx={{
          color: menuItemTextColor ?? '#f2f4f7',
          '&:hover': {
            backgroundColor: '#1F2A3A',
          },
          height: menuItemsHeight[menuItemHeight],
          width: menuItemCustomWidth ?? menuItemsWidth[menuItemWidth],
          padding: '12px',
          gap: '8px',
        }}
        style={menuItemStyle}
        onClick={() => {
          if (!item.disabled) {
            if (item.onClick) item.onClick();
            handleClose();
          }
        }}
        aria-live="polite"
        role="menuItem "
        disabled={item.disabled}
      >
        {item.label}
      </MenuItem>
    );

    return renderTooltipWrapper
      ? renderTooltipWrapper(menuItem, item.disabled)
      : menuItem;
  };
  useEffect(() => {
  if (!open) return;

  const handleCloseOnScroll = () => handleClose();

  // Close dropdown immediately on scroll or resize
  window.addEventListener('scroll', handleCloseOnScroll, true);
  window.addEventListener('resize', handleCloseOnScroll);

  return () => {
    window.removeEventListener('scroll', handleCloseOnScroll, true);
    window.removeEventListener('resize', handleCloseOnScroll);
  };
  }, [open]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  return (
    <div className="aur-drop-down" id={elementID}>
      {/* Hidden element for screen readers with the hint text */}
      <span id={hintId} style={{display: 'none'}}>
        {`${t('common.interactToViewMore')} ${title} ${t('common.options')}`}
      </span>
      <Button
        id="dropdown-button"
        className={`button-${type} button${size}_Size ${classNames}`}
        variant={variant}
        aria-haspopup="true"
        aria-expanded={false}
        ref={menuRef}
        aria-controls={open ? 'dropdown-menu' : undefined}
        aria-label={!buttonText ? hintText ?? t('common.dropdownOptionHints') : undefined}
        onClick={handleClick}
        startIcon={startIcon}
        endIcon={endIcon}
        title={title}
        {...buttonProps}
        sx={{
          '& .MuiButton-startIcon': {
            marginRight: '0px', // Adjust spacing
            marginLeft: '0px',
            // Add any other styles here
          },
          minWidth: '0px',
        }}
        data-testid={elementID}
      >
        {showLoader ? (
          <AurLoadingIndicator size={24} className="progress-icon" />
        ) : (
          buttonText && (
            <Typography className={`typo-text button${size}_size button-text`}>
              {buttonTitle && (
                <span className={buttonTitleClass}>{buttonTitle}&nbsp;</span>
              )}
              <span>{buttonText}</span>
            </Typography>
          )
        )}
        {children}
      </Button>
      {anchorEl && (
      <Menu
       id="dropdown-menu"
       className={`aurDropDownMenu ${menuClassNames}`}
       aria-labelledby={ariaLabelledby}
       role={dropdownRole}
       aria-describedby={hintId}
       anchorEl={anchorEl}
       open={open}
       onClose={handleClose}
       {...menuProps}
       anchorOrigin={{
        vertical: 'bottom',
        horizontal: menuDirectionLeft ? `left` : `right`,
       }}
       transformOrigin={{
       vertical: 'top',
       horizontal: menuDirectionLeft ? `left` : `right`,
       }}
       disableScrollLock
    >
      {menuItems.map((item, index) => renderMenuItem(item, index))}
    </Menu>
  )}

    </div>
  );
};
